/**
 * Tiny in-memory event bus for notes-mark restoration.
 *
 * Why this exists: `card-notes-cleanup.ts` used to fire a
 * `window.dispatchEvent(new CustomEvent('notes-marks-restored', ...))`
 * so external observers (devtools, analytics) could listen via
 * `window.addEventListener`. That's web-only — `window` is not defined
 * in React Native. Routing through this module keeps the contract
 * platform-agnostic: subscribers register listeners directly here, and
 * the web shim (`src/sync/web/notes-marks-window-bridge.ts`) bridges to
 * `window.dispatchEvent` to preserve the old web public API.
 *
 * This module has zero side effects and zero browser/Node imports — it
 * is safe to import from the cross-platform sync engine.
 */

export type NotesMarksRestoredDetail = { docId: string };

type Listener = (detail: NotesMarksRestoredDetail) => void;

const listeners = new Set<Listener>();

/** Subscribe to notes-mark restoration events. Returns an unsubscribe fn. */
export function subscribeToNotesMarksRestored(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Fire a notes-mark restoration event to all subscribers. Internal use only. */
export function emitNotesMarksRestored(detail: NotesMarksRestoredDetail): void {
  for (const listener of listeners) {
    try {
      listener(detail);
    } catch (err) {
      // A bad listener must not break the others or the caller.
      console.error('[notes-marks-events] listener error', err);
    }
  }
}
